import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useMemo } from 'react';
import {
  ArrowLeft, CheckCircle2, XCircle, AlertTriangle,
  ChevronDown, ChevronRight, Send, ShieldCheck, Play, Filter, Square, Download, Eye,
} from 'lucide-react';
import { format } from 'date-fns';
import { reconApi, type ReconRow } from '@/shared/api';
import { StatusPill } from '../components/StatusPill';
import { ResolutionPill } from '../components/ResolutionPill';
import { LiveActivityFeed } from '../components/LiveActivityFeed';
import { JobTimingsPanel } from '../components/JobTimingsPanel';
import { ChunksPanel } from '../components/ChunksPanel';
import { TxnReconView } from '../components/TxnReconView';
import { ErrorBoundary } from '@/shared/ErrorBoundary';
import { useCurrentUser, useHasRole } from '@/shared/auth';

type StateFilter = 'all' | 'pending_maker' | 'pending_checker' | 'approved' | 'applied' | 'rejected';

export function ReconJobDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [stateFilter, setStateFilter] = useState<StateFilter>('all');
  // Activity feed visibility:
  //   - in-flight  → expanded by default (the user wants to watch progress)
  //   - failed     → expanded by default (the user needs to see why)
  //   - terminal OK → collapsed by default but available to expand if
  //                   the user wants to see chunk timings, costs, etc.
  // Default chosen at first render based on whatever data we have; user
  // can toggle from there. We use undefined as "follow the default" so
  // re-renders don't force the feed back open on every refetch.
  const [activityOverride, setActivityOverride] = useState<boolean | null>(null);
  const queryClient = useQueryClient();
  const apiBaseUrl = import.meta.env.VITE_API_BASE_URL as string;

  const { data, isLoading } = useQuery({
    queryKey: ['recon-job', id],
    queryFn: () => reconApi.get(id!),
    refetchInterval: (q) => {
      const s = q.state.data?.status;
      return s === 'queued' || s === 'processing' ? 5_000 : false;
    },
  });

  // ── Shared-DP merge: find sibling jobs with the same DP ID ──
  // The DP file is centralized, so two jobs sharing a dp_client_id ran against
  // the same depository data with different PF files. We merge them so the
  // operator sees the union of both PFs against the single shared DP.
  const dpClientIdForMerge = data?.summary?.dp_client_id ?? null;
  const { data: jobList } = useQuery({
    queryKey: ['recon-jobs'],
    queryFn: () => reconApi.list(),
    enabled: !!dpClientIdForMerge,
  });
  const siblingIds = useMemo(() => {
    if (!dpClientIdForMerge || !jobList?.rows || !data) return [];
    const myPfId = data.summary?.pf_client_id ?? null;
    // Candidates: other jobs with the SAME DP ID but a DIFFERENT PF ID.
    // (Same PF+DP re-runs are NOT siblings — they'd add nothing and would
    //  wrongly trigger the shared-DP merge.) Keep one representative per
    //  distinct PF ID (the most recent), so re-runs of the same other PF
    //  aren't merged multiple times.
    const byPfId = new Map<string, { id: string; updated_at: string }>();
    for (const j of jobList.rows) {
      if (j.id === data.id) continue;
      if (j.summary?.dp_client_id !== dpClientIdForMerge) continue;
      const pf = j.summary?.pf_client_id;
      if (!pf || pf === myPfId) continue; // must be a DIFFERENT PF ID
      if (!(j.status === 'awaiting_review' || j.status === 'completed')) continue;
      const prev = byPfId.get(pf);
      if (!prev || new Date(j.updated_at) > new Date(prev.updated_at)) {
        byPfId.set(pf, { id: j.id, updated_at: j.updated_at });
      }
    }
    return [...byPfId.values()].map(v => v.id);
  }, [dpClientIdForMerge, jobList, data]);

  const { data: siblingDetails } = useQuery({
    queryKey: ['recon-job-siblings', id, siblingIds],
    queryFn: () => Promise.all(siblingIds.map(sid => reconApi.get(sid))),
    enabled: siblingIds.length > 0,
  });

  // Cancel mutation — declared at the top of the component (NOT after the
  // early returns below) so the hook count stays stable across renders.
  // Calling useMutation only after data arrives would violate the Rules
  // of Hooks: when isLoading is true we'd return early at line ~30 BEFORE
  // this hook ran, then on the next render we'd run an extra hook — React
  // catches that mismatch and crashes the page (blank screen).
  //
  // `id ?? ''` is safe because the mutation is never invoked until data
  // is present (the Cancel button only renders inside `data && isInFlight`).
  const cancelMutation = useMutation({
    mutationFn: () => reconApi.cancel(id ?? '', 'Cancelled by user from job detail page'),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['recon-job', id] });
      void queryClient.invalidateQueries({ queryKey: ['recon-jobs'] });
    },
  });

  // Rerun-mismatches mutation — re-extracts DP statements and updates
  // only the non-MATCH rows from the previous run. Same cost as a fresh
  // job but preserves the MATCH rows' reviewer state and the job id /
  // URL the user is currently on. Available only on terminal jobs of
  // transactions_v1 format.
  const rerunMutation = useMutation({
    mutationFn: () => reconApi.rerunMismatches(id ?? ''),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['recon-job', id] });
      void queryClient.invalidateQueries({ queryKey: ['recon-jobs'] });
    },
  });

  if (isLoading) return <div className="max-w-7xl mx-auto px-6 py-8 text-axis-ash">Loading…</div>;
  if (!data) return <div className="max-w-7xl mx-auto px-6 py-8 text-axis-ash">Job not found.</div>;

  const mismatches = data.rows
    .filter(r => r.status !== 'MATCH')
    .filter(r => {
      if (stateFilter === 'all') return true;
      if (stateFilter === 'rejected') return r.resolution_state === 'rejected_by_checker' || r.resolution_state === 'rejected_by_maker';
      return r.resolution_state === stateFilter;
    });

  // Bucket counts for the filter chips. SLB_OUTSTANDING is real
  // economic state, not an extraction error, so excluded from the
  // 'all problems' count that drives the rerun-mismatches button —
  // rerunning extraction won't change anything for SLB rows.
  const counts = {
    all:             data.rows.filter(r => r.status !== 'MATCH' && r.status !== 'SLB_OUTSTANDING').length,
    pending_maker:   data.rows.filter(r => r.resolution_state === 'pending_maker'   && r.status !== 'MATCH' && r.status !== 'SLB_OUTSTANDING').length,
    pending_checker: data.rows.filter(r => r.resolution_state === 'pending_checker' && r.status !== 'MATCH' && r.status !== 'SLB_OUTSTANDING').length,
    approved:        data.rows.filter(r => r.resolution_state === 'approved').length,
    applied:         data.rows.filter(r => r.resolution_state === 'applied').length,
    rejected:        data.rows.filter(r =>
      r.resolution_state === 'rejected_by_checker' || r.resolution_state === 'rejected_by_maker',
    ).length,
  };

  const isInFlight = data.status === 'queued' || data.status === 'processing';
  const defaultExpanded = isInFlight || data.status === 'failed';
  // User's explicit toggle wins over the default. Once they expand or
  // collapse, we honour their choice until they navigate away.
  const showActivityFeed = activityOverride ?? defaultExpanded;

  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <Link to="/recon" className="text-sm text-axis-ash hover:text-axis-ink inline-flex items-center gap-1 mb-2">
            <ArrowLeft className="w-4 h-4" /> All reconciliations
          </Link>
          <h1 className="text-2xl font-semibold text-axis-ink">Reconciliation {data.id.slice(0, 12)}…</h1>
          <div className="text-sm text-axis-ash mt-1">
            {/* portfolio_as_on / dp_as_on are optional. Show '—' when
                a job was submitted without them (the parser still reads
                dates from inside the files, but those are not surfaced
                in this header line). */}
            Portfolio as on <strong>{data.portfolio_as_on ?? '—'}</strong> · DP as on{' '}
            <strong>{data.dp_as_on ?? '—'}</strong> · status <strong>{data.status}</strong>
          </div>
        </div>
        {/* Action buttons. Cancel shown only in flight; Rerun-mismatches
            shown only when terminal AND there are mismatched rows to
            refresh. The two are mutually exclusive (a job is either in
            flight or at rest). */}
        <div className="flex items-center gap-2">
          {isInFlight && (
            <button
              type="button"
              onClick={() => {
                if (confirm('Cancel this reconciliation? Any in-flight Claude calls will be aborted and the job will be marked failed.')) {
                  cancelMutation.mutate();
                }
              }}
              disabled={cancelMutation.isPending}
              className="inline-flex items-center gap-2 text-sm font-medium px-3.5 py-2 rounded-lg transition disabled:opacity-50"
              style={{
                background: 'color-mix(in srgb, var(--status-err) 12%, transparent)',
                color: 'var(--status-err)',
                border: '1px solid color-mix(in srgb, var(--status-err) 40%, transparent)',
              }}
            >
              <Square className="w-3.5 h-3.5" fill="currentColor" />
              {cancelMutation.isPending ? 'Cancelling…' : 'Cancel job'}
            </button>
          )}
          {/* Verify extraction — opens the side-by-side review page.
              Available on terminal transactions_v1 jobs. Display-only —
              the reviewer can see the PDF on the right and the
              extracted data + math on the left, with no editing.
              Useful before approving a run, especially when a chunk
              fails or when QTY_MISMATCH counts look suspicious. */}
          {!isInFlight
            && data.format_version === 'transactions_v1' && (
            <Link
              to={`/recon/${data.id}/verify`}
              className="inline-flex items-center gap-2 text-sm font-medium px-3.5 py-2 rounded-lg transition"
              style={{
                background: 'color-mix(in srgb, var(--status-info) 10%, transparent)',
                color: 'var(--status-info)',
                border: '1px solid color-mix(in srgb, var(--status-info) 35%, transparent)',
              }}
              title="Open side-by-side verify page: extracted data on the left, source PDF on the right, math drilldown per ISIN."
            >
              <Eye className="w-3.5 h-3.5" />
              Verify extraction
            </Link>
          )}
          {/* Rerun-mismatches button — visible on terminal transactions_v1
              jobs that have at least one mismatch. Hidden if the previous
              run produced 100% matches (nothing to refresh) or if the job
              was a holdings_v1 (not supported). */}
          {!isInFlight
            && data.format_version === 'transactions_v1'
            && counts.all > 0 && (
            <button
              type="button"
              onClick={() => {
                if (confirm(
                  `Rerun ${counts.all} mismatched ISIN${counts.all === 1 ? '' : 's'}?\n\n` +
                  `This re-extracts the DP statement(s) with the latest extractor and updates ONLY the rows that are not MATCH. ` +
                  `MATCH rows from this run (and any reviewer notes on them) will be preserved untouched.\n\n` +
                  `Cost: same as a fresh job (~$3-4) — DP extraction is the dominant cost and cannot be done per-ISIN.`,
                )) {
                  rerunMutation.mutate();
                }
              }}
              disabled={rerunMutation.isPending}
              className="inline-flex items-center gap-2 text-sm font-medium px-3.5 py-2 rounded-lg transition disabled:opacity-50"
              style={{
                background: 'color-mix(in srgb, var(--axis-maroon) 8%, transparent)',
                color: 'var(--axis-maroon)',
                border: '1px solid color-mix(in srgb, var(--axis-maroon) 35%, transparent)',
              }}
              title={`${counts.all} mismatch row${counts.all === 1 ? '' : 's'} will be refreshed. MATCH rows preserved.`}
            >
              <Play className="w-3.5 h-3.5" />
              {rerunMutation.isPending ? 'Queueing rerun…' : `Rerun ${counts.all} mismatch${counts.all === 1 ? '' : 'es'}`}
            </button>
          )}
          {/* Download mismatches CSV — visible on terminal transactions_v1
              jobs with at least one mismatch. Lets the reviewer see every
              txn the engine considered for the mismatched ISINs, side by
              side with the totals, to spot extraction errors (missing rows,
              inflated quantities, duplicates, wrong dates) by comparing
              to the source PDFs / Excel. Uses the shared axios api client
              so dev-bearer + prod-session cookies both work. */}
          {!isInFlight
            && data.format_version === 'transactions_v1'
            && counts.all > 0 && (
            <button
              type="button"
              onClick={async () => {
                try {
                  const { api } = await import('@/shared/api');
                  const resp = await api.get(`/api/recon/jobs/${data.id}/mismatches.csv`, { responseType: 'blob' });
                  const blob = new Blob([resp.data], { type: 'text/csv;charset=utf-8' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `recon-${data.id.slice(0, 12)}-mismatches.csv`;
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                  URL.revokeObjectURL(url);
                } catch (err) {
                  alert(`Download failed: ${(err as Error).message}`);
                }
              }}
              className="inline-flex items-center gap-2 text-sm font-medium px-3.5 py-2 rounded-lg transition"
              style={{
                background: 'color-mix(in srgb, var(--text-strong) 5%, transparent)',
                color: 'var(--text-strong)',
                border: '1px solid var(--glass-border)',
              }}
              title="Download a CSV of every PF and DP transaction the engine considered for the mismatched ISINs"
            >
              <Download className="w-3.5 h-3.5" />
              Download mismatches CSV
            </button>
          )}
          {/* Download QTY-mismatches xlsx — focused subset for sending to
              the client's recon team for cross-checking. Excel format
              with a Summary sheet (bucket counts + explanations) that
              answers 'why is your count different from ours' before the
              question gets asked.

              Shown for all transactions_v1 jobs (even those with 0
              QTY_MISMATCH rows — the file will still have the Summary
              sheet showing the breakdown), as long as the job isn't
              in-flight. Available on old jobs too since the data is
              persisted in recon_row. */}
          {!isInFlight && data.format_version === 'transactions_v1' && (
            <button
              type="button"
              onClick={async () => {
                try {
                  const { api } = await import('@/shared/api');
                  const resp = await api.get(`/api/recon/jobs/${data.id}/qty_mismatches.xlsx`, { responseType: 'blob' });
                  const blob = new Blob([resp.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `recon-${data.id.slice(0, 12)}-qty-mismatches.xlsx`;
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                  URL.revokeObjectURL(url);
                } catch (err) {
                  alert(`Download failed: ${(err as Error).message}`);
                }
              }}
              className="inline-flex items-center gap-2 text-sm font-medium px-3.5 py-2 rounded-lg transition"
              style={{
                background: 'color-mix(in srgb, var(--text-strong) 5%, transparent)',
                color: 'var(--text-strong)',
                border: '1px solid var(--glass-border)',
              }}
              title="Excel file with QTY mismatches only + bucket summary. Ready to share with the client team for cross-check."
            >
              <Download className="w-3.5 h-3.5" />
              Download QTY mismatches (Excel)
            </button>
          )}
          {/* Download activity log JSON — visible on any job that has
              progressed past 'queued'. Returns the full chronological
              event stream (indexer pass output, per-chunk extraction
              milestones, model calls, token counts, warnings). Useful
              for debugging which layer (indexer/reasoning vs per-chunk
              extraction) is producing a given mismatch. Server keeps
              event history in Redis for 24h, max 200 events; older
              jobs may return an empty array. */}
          {!isInFlight && (
            <button
              type="button"
              onClick={async () => {
                try {
                  const { api } = await import('@/shared/api');
                  const resp = await api.get(`/api/recon/jobs/${data.id}/activity.json`, { responseType: 'blob' });
                  const blob = new Blob([resp.data], { type: 'application/json;charset=utf-8' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `recon-${data.id.slice(0, 12)}-activity.json`;
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                  URL.revokeObjectURL(url);
                } catch (err) {
                  alert(`Download failed: ${(err as Error).message}`);
                }
              }}
              className="inline-flex items-center gap-2 text-sm font-medium px-3.5 py-2 rounded-lg transition"
              style={{
                background: 'color-mix(in srgb, var(--text-strong) 5%, transparent)',
                color: 'var(--text-strong)',
                border: '1px solid var(--glass-border)',
              }}
              title="Download the full activity log (indexer pass, chunk extraction events, model calls) as JSON. Useful for debugging extraction failures."
            >
              <Download className="w-3.5 h-3.5" />
              Download activity log
            </button>
          )}
        </div>
      </div>

      {/* Activity feed toggle — shown on every status. Expanded by default
          for in-flight and failed jobs (where the user wants to see what's
          happening / why it failed). Collapsed by default for completed
          and awaiting_review jobs but available on demand so an operator
          can audit chunk timings, costs, and the full pipeline trace. */}
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-xs uppercase tracking-[0.18em] text-axis-ash">
          Activity & chunks
        </h2>
        <button
          type="button"
          onClick={() => setActivityOverride(!showActivityFeed)}
          className="text-xs px-3 py-1.5 rounded-md transition hover:bg-axis-cloud-50"
          style={{ border: '1px solid var(--glass-border)', color: 'var(--text-strong)' }}
        >
          {showActivityFeed ? 'Hide' : 'Show'} activity log
        </button>
      </div>

      {/* Timings panel — always visible regardless of whether the
          activity log is expanded. Pulled out of LiveActivityFeed so
          the timing summary survives after the user collapses the
          event list (the common path on completed jobs). */}
      <JobTimingsPanel
        jobId={data.id}
        apiBaseUrl={apiBaseUrl}
        createdAt={data.created_at}
        startedAt={data.started_at ?? null}
        updatedAt={data.updated_at}
      />

      {showActivityFeed && (
        <LiveActivityFeed
          jobId={data.id}
          apiBaseUrl={apiBaseUrl}
          onTerminal={() => {
            // Worker signaled it's done; refetch the job to pick up the new
            // status, rows, and summary.
            void queryClient.invalidateQueries({ queryKey: ['recon-job', data.id] });
          }}
        />
      )}
      {/* Chunks panel — visualizes per-chunk DP extraction state.
          Shown unconditionally; it self-hides when no chunk events have
          been seen yet. The previous wrapping inside `showActivityFeed`
          meant the panel disappeared once the job hit awaiting_review
          and the user collapsed the activity log — but the chunk info
          is useful retrospectively too (which chunks succeeded, how
          many holdings each yielded), so the panel should stay visible. */}
      <ChunksPanel jobId={data.id} apiBaseUrl={apiBaseUrl} />

      {/* Summary cards (holdings_v1 only — TxnReconView ships its own
          txn-specific tiles for transactions_v1). */}
      {data.summary && data.format_version !== 'transactions_v1' && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
          <SummaryCard label="Total rows"    value={data.summary.total_rows} tone="neutral" />
          <SummaryCard label="Match rate"    value={`${data.summary.match_rate_pct.toFixed(1)}%`} tone="match" />
          <SummaryCard label="Qty mismatch"  value={data.summary.qty_mismatch} tone="warn" />
          <SummaryCard label="Only in PF"    value={data.summary.only_in_portfolio} tone="warn" />
          <SummaryCard label="Only in DP"    value={data.summary.only_in_dp} tone="mismatch" />
        </div>
      )}

      {/* Cost summary — visible once the job has completed and the worker
          has totalled spend. Hidden during in-flight phase (LiveActivityFeed
          shows running spend there). */}
      {/* Cost summary panel — shown once the job has terminated and we
          have totalled spend. Hidden during in-flight phase (LiveActivityFeed
          shows running spend there).

          Two display modes:
            - cost_breakdown present (new jobs): show total +
              indexer/extraction split + collapsible per-call drilldown
            - cost only (legacy jobs from before commit 3): show the
              single-line summary the old UI rendered

          The drilldown lets an operator see which chunk cost what
          and how that compares to typical (~$0.27/chunk). Useful for
          spotting cost outliers — a chunk that took 3× the normal
          spend probably had retries or a pathological page. */}
      {data.summary?.cost_breakdown && data.status !== 'processing' && data.status !== 'queued' ? (
        <CostPanel breakdown={data.summary.cost_breakdown} />
      ) : data.summary?.cost && data.status !== 'processing' && data.status !== 'queued' && (
        <div className="card p-4 mb-6">
          <div className="flex flex-wrap items-baseline gap-x-6 gap-y-2 text-sm">
            <div>
              <span className="text-xs uppercase tracking-wider text-axis-ash">Claude spend</span>
              <span className="ml-2 font-semibold text-axis-ink font-mono">
                {data.summary.cost.total_usd < 1
                  ? `$${data.summary.cost.total_usd.toFixed(4)}`
                  : `$${data.summary.cost.total_usd.toFixed(2)}`}
              </span>
            </div>
            <div className="text-axis-ash">
              <span>{data.summary.cost.calls}</span> Claude call{data.summary.cost.calls === 1 ? '' : 's'}
            </div>
            <div className="text-axis-ash">
              <span>{data.summary.cost.input_tokens.toLocaleString()}</span> in /
              <span> {data.summary.cost.output_tokens.toLocaleString()}</span> out tokens
            </div>
          </div>
        </div>
      )}

      {data.summary?.portfolio_warnings?.length ? (
        <div className="card p-4 mb-6 bg-amber-500/10 border-amber-400/30">
          <div className="flex gap-2 text-amber-200 font-medium text-sm mb-1">
            <AlertTriangle className="w-5 h-5" />
            Input file warnings
          </div>
          <ul className="list-disc pl-5 text-sm text-amber-200">
            {data.summary.portfolio_warnings.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </div>
      ) : null}

      {data.status === 'failed' && (
        <div className="card p-4 mb-6 bg-rose-500/10 border-rose-400/30 text-rose-300 text-sm">
          Pipeline failed: {data.error_message}
        </div>
      )}

      {/* ── BRANCH on format_version ───────────────────────────────────
       *  transactions_v1 jobs have a fundamentally different output
       *  shape (per-ISIN summaries + per-txn drill-down instead of a
       *  flat list of holdings mismatches). The TxnReconView component
       *  owns its own summary tiles, filters, and row rendering, so we
       *  short-circuit out of the holdings UI entirely. */}
      {data.format_version === 'transactions_v1' ? (
        (siblingDetails && siblingDetails.length > 0) ? (
          // ── Shared DP, different PF files ──
          // Render a SEPARATE, fully-independent reconciliation per PF file,
          // stacked on the same page. Each PF is reconciled against the SAME
          // (centralized) DP data. [primary, ...siblings], ordered by PF ID.
          (() => {
            const dpId = data.summary?.dp_client_id ?? null;
            const allJobs = [data, ...siblingDetails]
              .slice()
              .sort((a, b) => (a.summary?.pf_client_id ?? '').localeCompare(b.summary?.pf_client_id ?? ''));
            // Ascending PF ID: the EARLIER (lowest) PF ID is the expired/merged
            // client; the LATER (highest) PF ID is the active new client.
            const lastIdx = allJobs.length - 1;
            return (
              <div className="space-y-8">
                <div className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl"
                  style={{ background: 'color-mix(in srgb, #8b5cf6 10%, transparent)', border: '1px solid color-mix(in srgb, #8b5cf6 30%, transparent)' }}>
                  <span className="text-base leading-none" style={{ color: '#8b5cf6' }}>★</span>
                  <span className="text-[13px]" style={{ color: 'var(--text-strong)' }}>
                    <strong>This is a case of Karvy Overlapping Client</strong> — DP ID {dpId} is shared by {allJobs.length} PF
                    {allJobs.length === 1 ? ' file' : ' files'} ({allJobs.map(j => j.summary?.pf_client_id ?? '—').join(', ')}).
                    Each PF is reconciled separately below against the same centralized DP.
                  </span>
                </div>
                {allJobs.map((job, idx) => {
                  const isActive = idx === lastIdx;               // highest PF ID = active
                  const statusLabel = isActive ? 'Active' : 'Perged';
                  const dotColor    = isActive ? '#16a34a' : '#dc2626'; // green / red
                  const noteLabel   = isActive ? 'New Client ID' : 'PF ID';
                  return (
                    <div key={job.id}>
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-sm font-bold px-2.5 py-1 rounded-lg"
                          style={{ background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)', color: '#fff' }}>
                          PF ID {job.summary?.pf_client_id ?? '—'}
                        </span>
                        {/* Perged (red) / Active (green) status */}
                        <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-1 rounded-lg"
                          style={{
                            background: `color-mix(in srgb, ${dotColor} 12%, transparent)`,
                            color: dotColor,
                            border: `1px solid color-mix(in srgb, ${dotColor} 30%, transparent)`,
                          }}>
                          <span className="inline-block rounded-full"
                            style={{ width: '9px', height: '9px', background: dotColor }} />
                          {noteLabel} ({job.summary?.pf_client_id ?? '—'}) — {statusLabel}
                        </span>
                        <span className="text-xs" style={{ color: 'var(--text-400)' }}>
                          reconciled against shared DP {dpId} · file {idx + 1} of {allJobs.length}
                        </span>
                      </div>
                      <ErrorBoundary label="reconciliation"><TxnReconView data={job} /></ErrorBoundary>
                    </div>
                  );
                })}
              </div>
            );
          })()
        ) : (
          <ErrorBoundary label="reconciliation"><TxnReconView data={data} /></ErrorBoundary>
        )
      ) : (
        <>
          {/* Work queue filter chips */}
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <Filter className="w-4 h-4 text-axis-ash" />
            <FilterChip active={stateFilter === 'all'}             onClick={() => setStateFilter('all')}             label="All"             count={counts.all} />
            <FilterChip active={stateFilter === 'pending_maker'}   onClick={() => setStateFilter('pending_maker')}   label="Awaiting maker"   count={counts.pending_maker} />
            <FilterChip active={stateFilter === 'pending_checker'} onClick={() => setStateFilter('pending_checker')} label="Awaiting checker" count={counts.pending_checker} />
            <FilterChip active={stateFilter === 'approved'}        onClick={() => setStateFilter('approved')}        label="Approved"         count={counts.approved} />
            <FilterChip active={stateFilter === 'applied'}         onClick={() => setStateFilter('applied')}         label="Applied"          count={counts.applied} />
            <FilterChip active={stateFilter === 'rejected'}        onClick={() => setStateFilter('rejected')}        label="Rejected"         count={counts.rejected} />
          </div>

          <div className="card overflow-hidden">
            <div className="px-4 py-3 border-b border-axis-cloud-200 flex items-center justify-between">
              <h2 className="font-semibold text-axis-ink">
                Showing {mismatches.length} row(s)
              </h2>
              <span className="text-xs text-axis-ash">Classified with claude-opus-4-7</span>
            </div>
            <div className="divide-y divide-axis-cloud-200">
              {mismatches.length === 0 ? (
                <div className="px-4 py-10 text-center text-axis-ash">
                  <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-500 mb-2" />
                  No rows in this state.
                </div>
              ) : (
                mismatches.map((row) => <MismatchRow key={row.id} row={row} />)
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function FilterChip({ active, onClick, label, count }: {
  active: boolean; onClick: () => void; label: string; count: number;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-xs px-3 py-1.5 rounded-full inline-flex items-center gap-1.5 ${
        active
          ? 'bg-axis-maroon text-white'
          : 'bg-axis-cloud-100 text-axis-ash hover:bg-axis-cloud-200'
      }`}
    >
      {label}
      <span className={`tabular-nums ${active ? 'text-white' : 'text-axis-ink font-medium'}`}>
        {count}
      </span>
    </button>
  );
}

function SummaryCard({ label, value, tone }: {
  label: string; value: number | string;
  tone: 'match' | 'warn' | 'mismatch' | 'neutral';
}) {
  const toneCls = {
    match:    'border-emerald-400/30',
    warn:     'border-amber-400/30',
    mismatch: 'border-rose-400/30',
    neutral:  'border-white/[0.08]',
  }[tone];
  const accent = {
    match:    'text-emerald-300',
    warn:     'text-amber-300',
    mismatch: 'text-rose-300',
    neutral:  'text-ink-50',
  }[tone];
  return (
    <div className={`card p-4 ${toneCls}`}>
      <div className="text-[10px] text-ink-400 uppercase tracking-[0.18em]">{label}</div>
      <div className={`text-3xl font-bold mt-1.5 tabular-nums tracking-tight ${accent}`}>{value}</div>
    </div>
  );
}

function MismatchRow({ row }: { row: ReconRow }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="px-4 py-3">
      <button
        type="button"
        className="w-full flex items-center gap-3 text-left"
        onClick={() => setExpanded(e => !e)}
      >
        {expanded ? <ChevronDown className="w-4 h-4 text-axis-ash" /> : <ChevronRight className="w-4 h-4 text-axis-ash" />}
        <div className="flex-1 grid grid-cols-12 gap-3 items-center">
          <div className="col-span-3 font-mono text-xs text-axis-ash">{row.canonical_isin}</div>
          <div className="col-span-3 text-sm font-medium">{row.display_name}</div>
          <div className="col-span-1 text-right text-sm tabular-nums">{Number(row.portfolio_qty).toLocaleString()}</div>
          <div className="col-span-1 text-right text-sm tabular-nums">{Number(row.dp_qty).toLocaleString()}</div>
          <div className="col-span-1 text-right text-sm tabular-nums font-medium text-axis-maroon">
            {row.qty_diff > 0 ? '+' : ''}{Number(row.qty_diff).toLocaleString()}
          </div>
          <div className="col-span-1"><StatusPill status={row.status} /></div>
          <div className="col-span-2 text-right"><ResolutionPill state={row.resolution_state} /></div>
        </div>
      </button>

      {expanded && (
        <div className="mt-3 ml-7 pl-3 border-l-2 border-axis-cloud-200 space-y-4">
          <DpBalanceBreakdown row={row} />
          {row.classification && <AiClassificationBlock row={row} />}
          <MakerCheckerBlock row={row} />
        </div>
      )}
    </div>
  );
}

function DpBalanceBreakdown({ row }: { row: ReconRow }) {
  // Show only when there's meaningful DP data to break down.
  if (row.dp_closing_balance === null) return null;
  const free = Number(row.dp_free_balance ?? 0);
  const pledge = Number(row.dp_pledge_balance ?? 0);
  const demat = Number(row.dp_demat_balance ?? 0);
  const total = Number(row.dp_closing_balance);
  const opening = row.dp_opening_balance !== null ? Number(row.dp_opening_balance) : null;

  // Compute the share each component contributes to the total so we can
  // render a stacked bar. Avoid divide-by-zero when the holding is closed.
  const denom = total > 0 ? total : 1;
  const freePct = (free / denom) * 100;
  const pledgePct = (pledge / denom) * 100;
  const dematPct = (demat / denom) * 100;

  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-ink-400 mb-2">DP balance breakdown</div>
      <div className="grid grid-cols-4 gap-3 mb-3">
        <BalanceTile label="Free" value={free} tone="ok" />
        <BalanceTile label="Pledged" value={pledge} tone="warn" />
        <BalanceTile label="Demat / Transit" value={demat} tone="info" />
        <BalanceTile label="Total closing" value={total} tone="neutral" emphasis />
      </div>
      {/* Stacked bar — visualizes how the holding is split */}
      {total > 0 && (
        <div className="h-1.5 rounded-full overflow-hidden flex" style={{ background: 'var(--bg-hover)' }}>
          {freePct > 0 && (
            <div title={`Free: ${free.toLocaleString()} (${freePct.toFixed(1)}%)`}
                 style={{ width: `${freePct}%`, background: 'var(--status-ok)' }} />
          )}
          {pledgePct > 0 && (
            <div title={`Pledged: ${pledge.toLocaleString()} (${pledgePct.toFixed(1)}%)`}
                 style={{ width: `${pledgePct}%`, background: 'var(--status-warn)' }} />
          )}
          {dematPct > 0 && (
            <div title={`Demat: ${demat.toLocaleString()} (${dematPct.toFixed(1)}%)`}
                 style={{ width: `${dematPct}%`, background: 'var(--status-info)' }} />
          )}
        </div>
      )}
      {opening !== null && (
        <div className="text-xs text-ink-400 mt-2 font-mono tabular-nums">
          Opening: <span className="text-ink-50">{opening.toLocaleString()}</span> · Net change:{' '}
          <span className="text-ink-50">{(total - opening) > 0 ? '+' : ''}{(total - opening).toLocaleString()}</span>
        </div>
      )}
    </div>
  );
}

function BalanceTile({
  label, value, tone, emphasis,
}: {
  label: string; value: number; tone: 'ok' | 'warn' | 'info' | 'neutral'; emphasis?: boolean;
}) {
  const toneStyle = {
    ok:      { color: 'var(--status-ok)',   border: 'color-mix(in srgb, var(--status-ok) 40%, transparent)' },
    warn:    { color: 'var(--status-warn)', border: 'color-mix(in srgb, var(--status-warn) 40%, transparent)' },
    info:    { color: 'var(--status-info)', border: 'color-mix(in srgb, var(--status-info) 40%, transparent)' },
    neutral: { color: 'var(--text-strong)', border: 'var(--glass-border-h)' },
  }[tone];
  return (
    <div className="rounded-xl p-2.5"
         style={{
           background: 'var(--bg-hover)',
           border: `1px solid ${toneStyle.border}`,
         }}>
      <div className="text-[10px] uppercase tracking-[0.16em] text-ink-400">{label}</div>
      <div className={`mt-0.5 tabular-nums ${emphasis ? 'text-base font-bold' : 'text-sm font-semibold'}`}
           style={{ color: toneStyle.color }}>
        {value.toLocaleString()}
      </div>
    </div>
  );
}

function AiClassificationBlock({ row }: { row: ReconRow }) {
  if (!row.classification) return null;
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-axis-ash mb-1">
        AI classification · confidence {row.confidence ? (row.confidence * 100).toFixed(0) + '%' : '—'}
      </div>
      <div className="text-sm font-medium text-axis-ink">{row.root_cause_code}</div>
      <div className="text-sm text-axis-ash mt-1">{row.classification.reasoning}</div>

      <div className="mt-2">
        <div className="text-xs uppercase tracking-wider text-axis-ash mb-1">Evidence</div>
        <ul className="text-xs space-y-1">
          {row.classification.evidence.map((e, i) => (
            <li key={i} className="flex gap-2">
              <span className="font-mono px-1.5 py-0.5 rounded bg-axis-cloud-100 text-axis-ash">{e.source}</span>
              <span className="text-axis-ink">{e.detail}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-2">
        <div className="text-xs uppercase tracking-wider text-axis-ash mb-1">Proposed resolution</div>
        <div className="text-sm text-axis-ink">{row.classification.proposed_resolution_summary}</div>
      </div>

      {row.classification.human_review_checklist?.length > 0 && (
        <div className="mt-2">
          <div className="text-xs uppercase tracking-wider text-axis-ash mb-1">Reviewer checklist</div>
          <ul className="text-xs text-axis-ink list-disc pl-5 space-y-0.5">
            {row.classification.human_review_checklist.map((c, i) => <li key={i}>{c}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}

/**
 * Maker-checker workflow block.
 *
 * Renders the right affordances per role and per resolution_state:
 *
 *   pending_maker:
 *     - Maker (or higher): show propose form (Accept/Override/Reject + note)
 *     - Checker only: show "Waiting for a maker to propose"
 *
 *   pending_checker:
 *     - Show the maker's proposal in full
 *     - Checker (or higher) who is NOT the original maker: show Approve / Reject
 *     - Original maker: explainer + disabled state
 *
 *   approved:
 *     - Show full history
 *     - Checker (or higher) who is NOT the maker: show Apply button
 *
 *   rejected_*  or applied:
 *     - Show history, no actions
 */
function MakerCheckerBlock({ row }: { row: ReconRow }) {
  const user = useCurrentUser();
  const isMaker   = useHasRole('maker');
  const isChecker = useHasRole('checker');
  const isOriginalMaker = !!user && user.actor_id === row.proposed_by;

  return (
    <div className="pt-3 border-t border-axis-cloud-200">
      <div className="text-xs uppercase tracking-wider text-axis-ash mb-2 inline-flex items-center gap-2">
        <ShieldCheck className="w-3.5 h-3.5" /> Maker–checker workflow
      </div>

      {/* History */}
      <div className="space-y-2">
        {row.proposed_by && (
          <HistoryEntry
            label="Maker proposal"
            actor={row.proposed_by}
            ts={row.proposed_at}
            note={row.proposed_resolution_note}
            extra={
              <span className="text-xs text-axis-ash">
                action: <strong>{row.proposed_action}</strong>
                {row.proposed_root_cause_code && (
                  <> · override → <strong>{row.proposed_root_cause_code}</strong></>
                )}
              </span>
            }
          />
        )}
        {row.checked_by && (
          <HistoryEntry
            label={row.checker_decision === 'approved' ? 'Checker approved' : 'Checker rejected'}
            actor={row.checked_by}
            ts={row.checked_at}
            note={row.checker_note}
            tone={row.checker_decision === 'approved' ? 'ok' : 'warn'}
          />
        )}
        {row.applied_by && (
          <HistoryEntry
            label="Applied"
            actor={row.applied_by}
            ts={row.applied_at}
            tone="ok"
          />
        )}
      </div>

      {/* Affordances */}
      <div className="mt-3">
        {row.resolution_state === 'pending_maker' && (
          isMaker
            ? <ProposeForm row={row} />
            : <Disabled message="Awaiting a maker to propose a resolution." />
        )}
        {row.resolution_state === 'pending_checker' && (
          isChecker
            ? isOriginalMaker
              ? <Disabled message="You proposed this — a different checker must approve or reject." />
              : <ApproveForm row={row} />
            : <Disabled message="Awaiting a checker to approve or reject the maker proposal." />
        )}
        {row.resolution_state === 'approved' && (
          isChecker
            ? isOriginalMaker
              ? <Disabled message="You proposed this — a different checker must apply." />
              : <ApplyButton row={row} />
            : <Disabled message="Approved. Awaiting apply." />
        )}
        {(row.resolution_state === 'rejected_by_checker' || row.resolution_state === 'rejected_by_maker') && (
          isMaker
            ? <Disabled message="Rejected. A new maker proposal will reopen the workflow." />
            : <Disabled message="Rejected." />
        )}
        {row.resolution_state === 'applied' && (
          <Disabled message="Applied. No further action." />
        )}
      </div>
    </div>
  );
}

function HistoryEntry({ label, actor, ts, note, extra, tone }: {
  label: string;
  actor: string | null;
  ts: string | null;
  note?: string | null;
  extra?: React.ReactNode;
  tone?: 'ok' | 'warn';
}) {
  return (
    <div className={`text-xs flex gap-3 p-2 rounded ${
      tone === 'ok' ? 'bg-emerald-500/10' : tone === 'warn' ? 'bg-rose-500/10' : 'bg-axis-cloud-50'
    }`}>
      <div className="flex flex-col">
        <span className="text-axis-ink font-medium">{label}</span>
        <span className="text-axis-ash">
          {actor && <>by <span className="font-mono">{actor}</span></>}
          {ts && <> · {format(new Date(ts), 'd MMM HH:mm:ss.SSS')}</>}
        </span>
      </div>
      <div className="flex-1">
        {extra}
        {note && <div className="text-axis-ash mt-1 italic">"{note}"</div>}
      </div>
    </div>
  );
}

function ProposeForm({ row }: { row: ReconRow }) {
  const qc = useQueryClient();
  const [action, setAction] = useState<'accept_proposal' | 'override' | 'reject'>('accept_proposal');
  const [overrideCode, setOverrideCode] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setSubmitting(true); setError(null);
    try {
      await reconApi.propose(row.id, {
        proposed_action: action,
        proposed_root_cause_code: action === 'override' && overrideCode ? overrideCode : undefined,
        note: note.trim() || undefined,
      });
      qc.invalidateQueries({ queryKey: ['recon-job'] });
    } catch (err) {
      setError(extractError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-2">
      <div className="text-xs uppercase tracking-wider text-axis-ash">Your proposal</div>
      <div className="flex gap-2 text-xs">
        <RadioButton label="Accept AI proposal" checked={action === 'accept_proposal'} onClick={() => setAction('accept_proposal')} />
        <RadioButton label="Override"           checked={action === 'override'}         onClick={() => setAction('override')} />
        <RadioButton label="Reject"             checked={action === 'reject'}           onClick={() => setAction('reject')} />
      </div>
      {action === 'override' && (
        <input
          type="text"
          placeholder="New root_cause_code (e.g. OFFLINE_TRANSFER_NOT_REGISTERED)"
          className="w-full text-sm p-2 border border-axis-cloud-200 rounded-md font-mono focus:ring-2 focus:ring-axis-maroon focus:outline-none"
          value={overrideCode}
          onChange={(e) => setOverrideCode(e.target.value.toUpperCase())}
        />
      )}
      <textarea
        placeholder="Note for the checker (recommended)…"
        className="w-full text-sm p-2 border border-axis-cloud-200 rounded-md focus:ring-2 focus:ring-axis-maroon focus:outline-none"
        rows={2}
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />
      {error && <ErrorMsg msg={error} />}
      <div className="flex justify-end">
        <button type="button" className="btn-primary" disabled={submitting || (action === 'override' && !overrideCode)} onClick={submit}>
          <Send className="w-4 h-4" />
          {submitting ? 'Submitting…' : 'Submit to checker'}
        </button>
      </div>
    </div>
  );
}

function ApproveForm({ row }: { row: ReconRow }) {
  const qc = useQueryClient();
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState<'approved' | 'rejected' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const decide = async (decision: 'approved' | 'rejected') => {
    setSubmitting(decision); setError(null);
    try {
      await reconApi.approve(row.id, { checker_decision: decision, note: note.trim() || undefined });
      qc.invalidateQueries({ queryKey: ['recon-job'] });
    } catch (err) {
      setError(extractError(err));
    } finally {
      setSubmitting(null);
    }
  };

  return (
    <div className="space-y-2">
      <div className="text-xs uppercase tracking-wider text-axis-ash">Checker decision</div>
      <textarea
        placeholder="Checker note (recommended)…"
        className="w-full text-sm p-2 border border-axis-cloud-200 rounded-md focus:ring-2 focus:ring-axis-maroon focus:outline-none"
        rows={2}
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />
      {error && <ErrorMsg msg={error} />}
      <div className="flex justify-end gap-2">
        <button type="button" className="btn-secondary" disabled={submitting !== null} onClick={() => decide('rejected')}>
          <XCircle className="w-4 h-4" /> Reject (back to maker)
        </button>
        <button type="button" className="btn-primary" disabled={submitting !== null} onClick={() => decide('approved')}>
          <CheckCircle2 className="w-4 h-4" />
          {submitting === 'approved' ? 'Approving…' : 'Approve'}
        </button>
      </div>
    </div>
  );
}

function ApplyButton({ row }: { row: ReconRow }) {
  const qc = useQueryClient();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const apply = async () => {
    setSubmitting(true); setError(null);
    try {
      await reconApi.apply(row.id);
      qc.invalidateQueries({ queryKey: ['recon-job'] });
    } catch (err) {
      setError(extractError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-axis-ash mb-2">Apply</div>
      <p className="text-xs text-axis-ash mb-2">
        Marks the resolution as executed. For WRITE_BACK playbooks this will (once integration is live)
        push the change to the portfolio system.
      </p>
      {error && <ErrorMsg msg={error} />}
      <div className="flex justify-end">
        <button type="button" className="btn-primary" disabled={submitting} onClick={apply}>
          <Play className="w-4 h-4" />
          {submitting ? 'Applying…' : 'Apply'}
        </button>
      </div>
    </div>
  );
}

function RadioButton({ label, checked, onClick }: { label: string; checked: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1.5 rounded border text-xs ${
        checked
          ? 'border-axis-maroon bg-axis-maroon/5 text-axis-maroon font-medium'
          : 'border-axis-cloud-200 text-axis-ash hover:border-axis-cloud-300'
      }`}
    >
      {label}
    </button>
  );
}

function Disabled({ message }: { message: string }) {
  return (
    <div className="text-xs text-axis-ash bg-axis-cloud-50 rounded p-2">{message}</div>
  );
}

function ErrorMsg({ msg }: { msg: string }) {
  return (
    <div className="text-xs text-rose-300 bg-rose-500/10 border border-rose-400/30 rounded p-2 flex gap-2">
      <XCircle className="w-4 h-4 shrink-0" />
      <span>{msg}</span>
    </div>
  );
}

function extractError(err: unknown): string {
  if (typeof err === 'object' && err !== null && 'response' in err) {
    const r = (err as { response?: { data?: { violations?: Array<{ message: string }>; error?: string; detail?: unknown } } }).response;
    if (r?.data?.violations?.length) {
      return r.data.violations.map(v => v.message).join('; ');
    }
    if (r?.data?.error) return r.data.error;
  }
  return (err as Error).message ?? 'unknown error';
}

/* ─── Cost breakdown panel ─────────────────────────────────────────────
 *
 * Renders the structured cost shape that the worker writes to
 * recon_job.summary.cost_breakdown. Shows:
 *
 *   - Total spend (dollars, 2 or 4 decimals depending on magnitude)
 *   - Indexer subtotal (reasoning purpose) — single ISIN-enumeration pass per DP
 *   - Extraction subtotal — per-chunk structured extraction calls
 *   - Collapsible per-call drilldown with label, model, latency, in/out tokens, cost
 *
 * Why split by purpose: lets the operator see at a glance which step
 * dominates cost. Today extraction is ~75% of spend; if that ratio
 * shifts, something changed (longer DP, more chunks, retries).
 *
 * Why a per-call drilldown: spotting outliers. A normal chunk costs
 * ~$0.27. A chunk that costs $0.80 had ~3× the normal output — usually
 * a pathological page with many sub-sections. Visible here, not
 * silently buried in the total.
 */
function fmtUsd(n: number): string {
  return n < 1 ? `$${n.toFixed(4)}` : `$${n.toFixed(2)}`;
}

function CostPanel({ breakdown }: {
  breakdown: {
    total_usd: number;
    total_calls: number;
    total_input_tokens: number;
    total_output_tokens: number;
    by_purpose: {
      reasoning:  { calls: number; cost_usd: number; input_tokens: number; output_tokens: number };
      extraction: { calls: number; cost_usd: number; input_tokens: number; output_tokens: number };
    };
    calls: Array<{
      purpose: 'reasoning' | 'extraction';
      label: string;
      model: string;
      cost_usd: number;
      input_tokens: number;
      output_tokens: number;
      elapsed_ms: number;
    }>;
  };
}) {
  const [open, setOpen] = useState(false);
  const ind = breakdown.by_purpose.reasoning;
  const ext = breakdown.by_purpose.extraction;
  return (
    <div className="card p-4 mb-6">
      <div className="flex flex-wrap items-baseline gap-x-6 gap-y-2 text-sm">
        <div>
          <span className="text-xs uppercase tracking-wider text-axis-ash">Total spend</span>
          <span className="ml-2 font-semibold text-axis-ink font-mono">{fmtUsd(breakdown.total_usd)}</span>
        </div>
        <div>
          <span className="text-xs uppercase tracking-wider text-axis-ash">Indexer</span>
          <span className="ml-2 font-mono text-axis-ink">{fmtUsd(ind.cost_usd)}</span>
          <span className="ml-1 text-axis-ash text-xs">({ind.calls} call{ind.calls === 1 ? '' : 's'})</span>
        </div>
        <div>
          <span className="text-xs uppercase tracking-wider text-axis-ash">Extraction</span>
          <span className="ml-2 font-mono text-axis-ink">{fmtUsd(ext.cost_usd)}</span>
          <span className="ml-1 text-axis-ash text-xs">
            ({ext.calls} chunk{ext.calls === 1 ? '' : 's'}
            {ext.calls > 0 ? `, avg ${fmtUsd(ext.cost_usd / ext.calls)}/chunk` : ''})
          </span>
        </div>
        <div className="text-axis-ash">
          <span>{breakdown.total_input_tokens.toLocaleString()}</span> in /
          <span> {breakdown.total_output_tokens.toLocaleString()}</span> out tokens
        </div>
      </div>
      <button
        type="button"
        className="mt-3 text-xs text-axis-ash hover:text-axis-ink flex items-center gap-1"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
      >
        <span className={`inline-block transition-transform ${open ? 'rotate-90' : ''}`}>▸</span>
        {open ? 'Hide per-call breakdown' : `Show per-call breakdown (${breakdown.calls.length} call${breakdown.calls.length === 1 ? '' : 's'})`}
      </button>
      {open && (
        <div className="mt-3 overflow-x-auto rounded-md border border-axis-cloud-200">
          <table className="w-full text-xs">
            <thead style={{ background: 'var(--bg-elevated)', color: 'var(--text-strong)' }}>
              <tr>
                <th className="px-3 py-2 text-left text-[11px] uppercase tracking-wider font-semibold">Purpose</th>
                <th className="px-3 py-2 text-left text-[11px] uppercase tracking-wider font-semibold">Label</th>
                <th className="px-3 py-2 text-left text-[11px] uppercase tracking-wider font-semibold">Model</th>
                <th className="px-3 py-2 text-right text-[11px] uppercase tracking-wider font-semibold">In</th>
                <th className="px-3 py-2 text-right text-[11px] uppercase tracking-wider font-semibold">Out</th>
                <th className="px-3 py-2 text-right text-[11px] uppercase tracking-wider font-semibold">Latency</th>
                <th className="px-3 py-2 text-right text-[11px] uppercase tracking-wider font-semibold">Cost</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-axis-cloud-200">
              {breakdown.calls.map((c, idx) => (
                <tr
                  key={idx}
                  style={{
                    background: c.purpose === 'reasoning'
                      ? 'color-mix(in srgb, var(--status-info) 6%, transparent)'
                      : 'color-mix(in srgb, var(--status-ok) 6%, transparent)',
                    color: 'var(--text-strong)',
                  }}
                >
                  <td className="px-3 py-2">
                    <span
                      className="font-semibold"
                      style={{ color: c.purpose === 'reasoning' ? 'var(--status-info)' : 'var(--status-ok)' }}
                    >
                      {c.purpose}
                    </span>
                  </td>
                  <td className="px-3 py-2 max-w-[280px] truncate" title={c.label}>{c.label}</td>
                  <td className="px-3 py-2 font-mono text-[11px]" style={{ color: 'var(--text-200)' }}>{c.model}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{c.input_tokens.toLocaleString()}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{c.output_tokens.toLocaleString()}</td>
                  <td className="px-3 py-2 text-right tabular-nums" style={{ color: 'var(--text-200)' }}>
                    {(c.elapsed_ms / 1000).toFixed(1)}s
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums font-mono">{fmtUsd(c.cost_usd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
